import { supabase } from '../lib/supabase';
import embeddingService from './embeddingService';
import confidenceScoring from './confidenceScoring';
import { getResponseWithConfidence } from './intelligentRetrieval';

/**
 * Hybrid Retrieval Service
 * Combines semantic search (embeddings) + rule compliance + confidence scoring
 * for context-aware response selection with quality guardrails
 */

const DEFAULT_SEMANTIC_WEIGHT = 0.4;
const DEFAULT_RULE_WEIGHT = 0.3;
const DEFAULT_CONFIDENCE_WEIGHT = 0.2;
const DEFAULT_PERFORMANCE_WEIGHT = 0.1;

/**
 * Hybrid retrieval with semantic search, rules, confidence, and performance
 * @param {Object} request - Search request
 * @param {string} request.query - Search query text
 * @param {string} request.type - Response type
 * @param {Array<string>} request.requiredRules - Rule IDs that must be satisfied
 * @param {Object} request.context - Context for confidence scoring
 * @param {Object} request.weights - Custom weight distribution
 * @param {number} request.minThreshold - Minimum combined score threshold
 * @returns {Promise<Object>} - Best matching response with scoring breakdown
 */
export const hybridRetrieve = async (request) => {
  const {
    query,
    type,
    requiredRules = [],
    context = {},
    weights = {},
    minThreshold = 70,
    limit = 10
  } = request;

  const {
    semantic = DEFAULT_SEMANTIC_WEIGHT,
    rule = DEFAULT_RULE_WEIGHT,
    confidence = DEFAULT_CONFIDENCE_WEIGHT,
    performance = DEFAULT_PERFORMANCE_WEIGHT
  } = weights;

  try {
    // Step 1: Semantic search using embeddings
    const semanticMatches = await embeddingService.findSimilarResponses(query, {
      type,
      threshold: 0.6, // Lower threshold for candidates
      limit: 50 // Get more candidates for scoring
    });

    if (semanticMatches.length === 0) {
      // No semantic matches - fallback to AI generation
      return await getResponseWithConfidence({
        type,
        query,
        ...context
      });
    }

    // Step 2: Check rule compliance for candidates
    const candidateIds = semanticMatches.map(m => m.id);
    const ruleCompliance = await getRuleComplianceScores(candidateIds, requiredRules);

    // Step 3: Get performance stats for candidates
    const performanceStats = await getPerformanceScores(candidateIds);

    // Step 4: Calculate confidence scores
    const confidenceScores = semanticMatches.map(match => {
      const responseUnit = match;
      return {
        responseUnitId: match.id,
        score: confidenceScoring.calculateConfidence(responseUnit, context)
      };
    });

    // Step 5: Combine all scores with weights
    const scoredCandidates = semanticMatches.map(match => {
      const semanticScore = match.similarity * 100; // Convert 0-1 to 0-100
      const ruleScore = ruleCompliance[match.id] || 0;
      const confidenceScore = confidenceScores.find(c => c.responseUnitId === match.id)?.score?.score || 0;
      const performanceScore = performanceStats[match.id] || 50; // Default neutral score

      const combinedScore = 
        (semanticScore * semantic) +
        (ruleScore * rule) +
        (confidenceScore * confidence) +
        (performanceScore * performance);

      return {
        ...match,
        scores: {
          semantic: Math.round(semanticScore),
          rule: Math.round(ruleScore),
          confidence: Math.round(confidenceScore),
          performance: Math.round(performanceScore),
          combined: Math.round(combinedScore)
        },
        scoreBreakdown: {
          semantic: { score: Math.round(semanticScore), weight: semantic },
          rule: { score: Math.round(ruleScore), weight: rule },
          confidence: { score: Math.round(confidenceScore), weight: confidence },
          performance: { score: Math.round(performanceScore), weight: performance }
        }
      };
    });

    // Sort by combined score
    scoredCandidates.sort((a, b) => b.scores.combined - a.scores.combined);

    // Check if best match meets threshold
    const bestMatch = scoredCandidates[0];

    if (!bestMatch || bestMatch.scores.combined < minThreshold) {
      // No high-quality match - fallback to AI generation
      console.log(`Best match score ${bestMatch?.scores?.combined || 0} below threshold ${minThreshold}. Generating fresh response...`);
      
      return await getResponseWithConfidence({
        type,
        query,
        ...context
      });
    }

    // Fetch full response unit data
    const { data: responseUnit, error } = await supabase
      .from('response_units')
      .select('*')
      .eq('id', bestMatch.id)
      .single();

    if (error) throw error;

    // Increment usage count
    await incrementUsageCount(bestMatch.id);

    return {
      ...responseUnit,
      retrievalMethod: 'hybrid',
      scores: bestMatch.scores,
      scoreBreakdown: bestMatch.scoreBreakdown,
      alternativesCount: scoredCandidates.length - 1,
      topAlternatives: scoredCandidates.slice(1, 4) // Top 3 alternatives
    };

  } catch (error) {
    console.error('Error in hybrid retrieval:', error);
    throw error;
  }
};

/**
 * Get rule compliance scores for response units
 * @param {Array<string>} responseUnitIds - Response unit IDs
 * @param {Array<string>} requiredRuleIds - Required rule IDs
 * @returns {Promise<Object>} - Map of responseUnitId -> compliance score
 */
const getRuleComplianceScores = async (responseUnitIds, requiredRuleIds) => {
  if (requiredRuleIds.length === 0) {
    // No rules required - everyone gets 100
    return responseUnitIds.reduce((acc, id) => ({ ...acc, [id]: 100 }), {});
  }

  try {
    const { data, error } = await supabase
      .from('response_rule_compliance')
      .select('response_unit_id, rule_id, complies')
      .in('response_unit_id', responseUnitIds)
      .in('rule_id', requiredRuleIds)
      .eq('complies', true);

    if (error) throw error;

    // Count compliance per response
    const complianceCounts = {};
    data.forEach(row => {
      if (!complianceCounts[row.response_unit_id]) {
        complianceCounts[row.response_unit_id] = 0;
      }
      complianceCounts[row.response_unit_id]++;
    });

    // Convert to percentage
    const scores = {};
    responseUnitIds.forEach(id => {
      const count = complianceCounts[id] || 0;
      scores[id] = Math.round((count / requiredRuleIds.length) * 100);
    });

    return scores;
  } catch (error) {
    console.error('Error getting rule compliance scores:', error);
    return {};
  }
};

/**
 * Get performance scores from outcome data
 * @param {Array<string>} responseUnitIds - Response unit IDs
 * @returns {Promise<Object>} - Map of responseUnitId -> performance score
 */
const getPerformanceScores = async (responseUnitIds) => {
  try {
    const { data, error } = await supabase
      .from('response_performance_stats')
      .select('response_unit_id, success_rate, avg_score, total_uses')
      .in('response_unit_id', responseUnitIds);

    if (error) throw error;

    const scores = {};
    data.forEach(row => {
      // Weight success rate and avg score
      // Success rate gets 60% weight, avg score gets 40%
      const score = (row.success_rate * 0.6) + (row.avg_score * 0.4);
      
      // Penalize responses with very few uses (not enough data)
      const confidenceMultiplier = Math.min(1, row.total_uses / 10);
      
      scores[row.response_unit_id] = Math.round(score * confidenceMultiplier);
    });

    // Fill in missing scores with neutral 50
    responseUnitIds.forEach(id => {
      if (!scores[id]) {
        scores[id] = 50; // Neutral score for responses without performance data
      }
    });

    return scores;
  } catch (error) {
    console.error('Error getting performance scores:', error);
    return {};
  }
};

/**
 * Increment usage count
 * @param {string} responseUnitId - Response unit ID
 */
const incrementUsageCount = async (responseUnitId) => {
  try {
    await supabase.rpc('increment_response_unit_usage_rpc', {
      response_unit_id: responseUnitId
    });
  } catch (error) {
    console.error('Error incrementing usage count:', error);
  }
};

/**
 * Simplified hybrid search for common use cases
 * @param {string} query - Search query
 * @param {string} type - Response type
 * @param {Object} context - Context for matching
 * @returns {Promise<Object>} - Best response
 */
export const quickHybridSearch = async (query, type, context = {}) => {
  // Define must-have rules by type
  const requiredRulesByType = {
    objection: ['rule_empathy_acknowledge', 'rule_probe_clarify', 'rule_value_evidence', 'rule_cta_specific'],
    script: ['rule_probe_clarify', 'rule_cta_specific'],
    hint: [], // No strict rules for hints
    micro_lesson: []
  };

  return await hybridRetrieve({
    query,
    type,
    requiredRules: requiredRulesByType[type] || [],
    context,
    minThreshold: type === 'objection' ? 75 : 70 // Higher threshold for objections
  });
};

/**
 * Search with custom rule requirements
 * @param {string} query - Search query
 * @param {string} type - Response type
 * @param {Array<string>} requiredRules - Rule IDs
 * @param {Object} context - Context for matching
 * @returns {Promise<Object>} - Best response
 */
export const ruleGuardedSearch = async (query, type, requiredRules, context = {}) => {
  return await hybridRetrieve({
    query,
    type,
    requiredRules,
    context,
    weights: {
      semantic: 0.3,
      rule: 0.5, // Higher weight on rule compliance
      confidence: 0.15,
      performance: 0.05
    },
    minThreshold: 80 // Strict threshold when using rule guards
  });
};

/**
 * Performance-optimized search (favors proven responses)
 * @param {string} query - Search query
 * @param {string} type - Response type
 * @param {Object} context - Context for matching
 * @returns {Promise<Object>} - Best performing response
 */
export const performanceOptimizedSearch = async (query, type, context = {}) => {
  return await hybridRetrieve({
    query,
    type,
    requiredRules: [],
    context,
    weights: {
      semantic: 0.2,
      rule: 0.2,
      confidence: 0.2,
      performance: 0.4 // Heavily favor proven performers
    },
    minThreshold: 65
  });
};

export default {
  hybridRetrieve,
  quickHybridSearch,
  ruleGuardedSearch,
  performanceOptimizedSearch
};
